import { GetReq, Headers, PostReq, ResponseMessages } from "src/utils/types";
import axios, { AxiosError } from "axios";
import { store } from "src/redux/store";
import { setPermissionErrorModal, logout } from "src/redux/slices/userSlice/UserSlice";
import { setHeaderToken as setHeaderTokenRedux, setSessionId, clearTokens } from "src/redux/slices/appSlice/AppSlice";
import { __DEV__, SECURITY_KEY } from "@/utils/constants";
import { mockDataEnabled } from "@/mockData/MockData";

export const BASE_URL = process.env.REACT_APP_API_URL || "";
export const WEBSITE_URL = process.env.REACT_APP_BASE_URL || "";
const REFRESH_JWT_URL = `${BASE_URL}/cmd/auth/refresh-jwt`;
const API_TIMEOUT_MS = 30000;

const JWT_REGEX = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
// test
function xorEncryptDecrypt(input: string): string {
  let output = "";
  for (let i = 0; i < input.length; i++) {
    output += String.fromCharCode(
      input.charCodeAt(i) ^ SECURITY_KEY.charCodeAt(i % SECURITY_KEY.length)
    );
  }
  return output;
}

export function tryDecryptToken(token: string): string {
  if (!token || typeof token !== "string") return token;
  
  __DEV__ && console.log("[Api] tryDecryptToken → starting, token length:", token.length);
  
  // Check if the token looks like base64
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  const looksLikeBase64 = base64Regex.test(token);
  
  __DEV__ && console.log("[Api] tryDecryptToken → looks like base64:", looksLikeBase64);
  
  // If it looks like base64, try to decode it first
  if (looksLikeBase64) {
    try {
      const decoded = atob(token);
      __DEV__ && console.log("[Api] tryDecryptToken → decoded from base64, length:", decoded.length);
      const decrypted = xorEncryptDecrypt(decoded);
      if (JWT_REGEX.test(decrypted)) {
        __DEV__ && console.log("[Api] tryDecryptToken → base64+XOR successful, is JWT");
        return decrypted;
      }
    } catch (_e) {
      __DEV__ && console.log("[Api] tryDecryptToken → base64 decode failed, trying direct XOR");
    }
  }
  
  // Fallback: try direct XOR decryption
  try {
    const decrypted = xorEncryptDecrypt(token);
    if (JWT_REGEX.test(decrypted)) {
      __DEV__ && console.log("[Api] tryDecryptToken → direct XOR successful, is JWT");
      return decrypted;
    }
  } catch (_e) {
    // ignore
  }
  
  __DEV__ && console.log("[Api] tryDecryptToken → not JWT after decrypt, using original");
  return token;
}

const headers: Headers = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

export const setHeaderToken = ({
  accessToken,
  refreshToken,
  userToken,
  customerIdEncrypted,
}: {
  accessToken: string;
  refreshToken?: string;
  userToken?: string;
  customerIdEncrypted?: string;
}) => {
  if (!accessToken) {
    delete headers.Authorization;
    // __DEV__ && console.log("[Api] setHeaderToken → cleared (no accessToken)");
    return;
  }
  isSessionExpired = false;
  headers.Authorization = `Bearer ${accessToken}`;
  store.dispatch(
    setHeaderTokenRedux({ accessToken, refreshToken, userToken, customerIdEncrypted }),
  );
  const newSessionId = crypto.randomUUID();
  store.dispatch(setSessionId(newSessionId));
  // __DEV__ &&
  //   console.log("[Api] setHeaderToken → set", {
  //     tokenLen: accessToken.length,
  //     hasRefresh: !!refreshToken,
  //     sessionId: newSessionId,
  //   });
};

export const clearAuthFromApi = () => {
  delete headers.Authorization;
  store.dispatch(clearTokens());
  store.dispatch(setSessionId(""));
  __DEV__ && console.log("[Api] clearAuthFromApi → headers + Redux cleared");
};

/** Sync Authorization header from Redux (e.g. after rehydrate from persist). */
const syncHeadersFromStore = () => {
  const token = store.getState()?.app?.accessToken;
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
    // __DEV__ && console.log("[Api] syncHeadersFromStore → restored Authorization from Redux");
  }
};

let activeRefreshPromise: Promise<any> | null = null;
let isSessionExpired = false;

/** After refresh-jwt returns 401: logout + clear auth, then show session-expired modal (logout clears modal if set first). */
const notifySessionExpiredAndLogout = (isUnauthorizedFromRefreshJwt: boolean) => {
  const appStrings = store.getState().app.strings;
  clearAuthFromApi();
  store.dispatch(logout());
  if (isUnauthorizedFromRefreshJwt) {
    store.dispatch(
      setPermissionErrorModal({
        title: appStrings?.t_session_expired_modal_title ?? "",
        body: appStrings?.t_session_expired_modal_body ?? "",
        footer: "",
        primaryButtonLabel: appStrings?.t_session_expired_modal_primary_button ?? "",
      })
    );
  } else {
    store.dispatch(
      setPermissionErrorModal({
        title: appStrings?.t_401_error_popup_title ?? "",
        body: appStrings?.t_401_error_popup_body ?? "",
        footer: "",
      })
    );
  }
};

const refreshTokenRequest = async () => {
  const refreshToken = store.getState()?.app?.refreshToken;
  if (!refreshToken) {
    __DEV__ && console.log("[Api] refreshTokenRequest → no refreshToken in store, throw");
    throw new Error("No refresh token");
  }
  __DEV__ && console.log("[Api] refreshTokenRequest → POST /cmd/auth/refresh-jwt");
  const response = await axios.post(
    REFRESH_JWT_URL,
    { refreshToken },
    { headers, timeout: API_TIMEOUT_MS }
  );
  const responseData = response.data;
  if (!responseData || typeof responseData !== "object") {
    __DEV__ && console.log("[Api] refreshTokenRequest → invalid response data");
    throw new Error("Invalid response data");
  }
  const rawToken =
    typeof responseData.AccessToken === "string"
      ? responseData.AccessToken
      : typeof responseData.accessToken === "string"
        ? responseData.accessToken
        : null;
  if (!rawToken) {
    __DEV__ && console.log("[Api] refreshTokenRequest → no AccessToken/accessToken in response");
    throw new Error("Missing or invalid tokens in response");
  }
  __DEV__ &&
    console.log("[Api] refreshTokenRequest → got rawToken, tryDecryptToken…", {
      rawLen: rawToken.length,
    });
  const newAccessToken = tryDecryptToken(rawToken);
  setHeaderToken({ accessToken: newAccessToken });
  __DEV__ && console.log("[Api] refreshTokenRequest → done, will retry original request");
  return responseData;
};

const refreshTokenOnce = async (): Promise<any> => {
  if (isSessionExpired) {
    __DEV__ && console.log("[Api] refreshTokenOnce → skip (session already expired) → logout + home");
    notifySessionExpiredAndLogout(true);
    throw new Error("Session expired - user is being logged out");
  }
  if (activeRefreshPromise) {
    __DEV__ && console.log("[Api] refreshTokenOnce → reusing in-flight refresh promise");
    return activeRefreshPromise;
  }
  __DEV__ && console.log("[Api] refreshTokenOnce → starting new refresh");
  activeRefreshPromise = refreshTokenRequest()
    .catch((error) => {
      __DEV__ &&
        console.log("[Api] refreshTokenOnce → refresh failed", {
          status: error?.response?.status,
          message: error?.message,
        });
      isSessionExpired = true;
      const isUnauthorizedRefresh =
        axios.isAxiosError(error) && error.response?.status === 401;
      notifySessionExpiredAndLogout(isUnauthorizedRefresh);
      throw error;
    })
    .finally(() => {
      activeRefreshPromise = null;
    });
  return activeRefreshPromise;
};

export const getAsync = async ({ fullUrl, url }: GetReq) => {
  syncHeadersFromStore();
  const requestUrl = fullUrl || `${BASE_URL}${url}`;
  const appStrings = store.getState().app.strings;

  // __DEV__ && console.log("[Api] getAsync → GET", requestUrl, { hasAuth: !!headers.Authorization });

  try {
    const response = await axios.get(requestUrl, { headers, timeout: API_TIMEOUT_MS });
    // __DEV__ && console.log("[Api] getAsync → GET success", requestUrl);
    return response.data;
  } catch (e: any) {
    // __DEV__ &&
    //   console.log("[Api] getAsync → failure", {
    //     url: requestUrl,
    //     status: e?.response?.status,
    //     data: e?.response?.data,
    //     code: e?.code,
    //     message: e?.message,
    //   });

    if (axios.isAxiosError(e)) {
      if (e.code === "ECONNABORTED") {
        store.dispatch(
          setPermissionErrorModal({
            title: appStrings.t_error_popup_title ?? "",
            body: appStrings.t_message_timeout ?? "",
            footer: "",
          })
        );
        throw new Error("Request timeout - server did not respond in time");
      }

      const status = e.response?.status;
      const shouldRefreshAndRetry =
        status === 401 ||
        status === 403 ||
        (e.code === "ERR_NETWORK" && !!headers.Authorization);

      if (e.code === "ERR_NETWORK" && !shouldRefreshAndRetry) {
        throw e;
      }

      if (shouldRefreshAndRetry) {
        // __DEV__ &&
        //   console.log("[Api] getAsync →", status ?? "ERR_NETWORK (auth)", "→ calling refreshTokenOnce then retry");
        try {
          await refreshTokenOnce();
          const newResponse = await axios.get(requestUrl, { headers, timeout: API_TIMEOUT_MS });
          // __DEV__ && console.log("[Api] getAsync → retry GET success", requestUrl);
          return newResponse.data;
        } catch (refreshError: any) {
          // __DEV__ &&
          //   console.log("[Api] getAsync → retry failure", {
          //     url: requestUrl,
          //     status: refreshError?.response?.status,
          //     data: refreshError?.response?.data,
          //     code: refreshError?.code,
          //     message: refreshError?.message,
          //   });
          throw refreshError;
        }
      }

      if (e.response) {
        const status = e.response.status;
        const errorMessage =
          e.response.data?.responseMessage ||
          e.response.data?.message ||
          appStrings.t_message_error;
        if (status === 403 && !isSessionExpired) {
          store.dispatch(
            setPermissionErrorModal({
              title: appStrings.t_401_error_popup_title ?? "",
              body: appStrings.t_401_error_popup_body ?? errorMessage,
              footer: "",
            })
          );
        } else if (status !== 401 && !isSessionExpired) {
          store.dispatch(
            setPermissionErrorModal({
              title: appStrings.t_error_popup_title ?? "",
              body: errorMessage,
              footer: "",
            })
          );
        }
        throw new Error(`API Error: ${status} - ${JSON.stringify(e.response.data)}`);
      }

      if (!isSessionExpired) {
        store.dispatch(
          setPermissionErrorModal({
            title: appStrings.t_error_popup_title ?? "",
            body: appStrings.t_message_network_error ?? "",
            footer: "",
          })
        );
      }
      throw new Error(
        "No response from server - check if server is running and BASE_URL is correct"
      );
    }

    if (!isSessionExpired) {
      store.dispatch(
        setPermissionErrorModal({
          title: appStrings.t_error_popup_title ?? "",
          body: appStrings.t_message_error ?? "",
          footer: "",
        })
      );
    }
    throw e;
  }
};

export const postAsync = async ({ fullUrl, url, body }: PostReq) => {
  syncHeadersFromStore();
  const requestUrl = fullUrl || `${BASE_URL}${url}`;

  // __DEV__ && console.log("[Api] postAsync → POST", requestUrl, { hasAuth: !!headers.Authorization });

  try {
    const response = await axios.post(requestUrl, body, { headers, timeout: API_TIMEOUT_MS });
    // __DEV__ && console.log("[Api] postAsync → POST success", requestUrl);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const errorData = error.response?.data;

      __DEV__ &&
        console.log("[Api] postAsync → failure", {
          url: requestUrl,
          status,
          responseData:
            errorData === undefined || errorData === ""
              ? "(empty/undefined)"
              : errorData,
          responseDataType: errorData === null ? "null" : typeof errorData,
          axiosCode: error.code,
          message: error.message,
          hasAuthorizationHeader: !!headers.Authorization,
          refreshTokenInStore: !!store.getState()?.app?.refreshToken,
        });

      const shouldRefreshAndRetry =
        status === 401 ||
        status === 403 ||
        (error.code === "ERR_NETWORK" && !!headers.Authorization);

      __DEV__ &&
        console.log("[Api] postAsync → shouldRefreshAndRetry", shouldRefreshAndRetry, {
          reason:
            status === 401
              ? "HTTP_401"
              : status === 403
                ? "HTTP_403"
                : error.code === "ERR_NETWORK" && !!headers.Authorization
                  ? "ERR_NETWORK_WITH_AUTH"
                  : "none",
        });

      if (error.code === "ERR_NETWORK" && !shouldRefreshAndRetry) {
        throw error;
      }

      if (shouldRefreshAndRetry) {
        __DEV__ &&
          console.log(
            "[Api] postAsync →",
            status ?? "ERR_NETWORK (auth)",
            "→ refreshTokenOnce then retry POST"
          );
        try {
          await refreshTokenOnce();
          __DEV__ &&
            console.log("[Api] postAsync → refresh OK, retrying POST", requestUrl);
          const newResponse = await axios.post(requestUrl, body, {
            headers,
            timeout: API_TIMEOUT_MS,
          });
          __DEV__ && console.log("[Api] postAsync → retry POST success", requestUrl);
          return newResponse.data;
        } catch (refreshError: any) {
          __DEV__ &&
            console.log("[Api] postAsync → refresh or retry failed", {
              url: requestUrl,
              isAxios: axios.isAxiosError(refreshError),
              status: refreshError?.response?.status,
              data: refreshError?.response?.data,
              code: refreshError?.code,
              message: refreshError?.message,
              name: refreshError?.name,
            });
          throw refreshError;
        }
      }

      if (error.response) {
        if (status === 400) {
          throw new Error(
            JSON.stringify({ error: true, status: 400, data: errorData })
          );
        }
        if (status === 403) {
          const errorsRestriction = [
            ResponseMessages.OTP_INVALID,
            ResponseMessages.USER_BLOCKED,
          ];
          if (!errorsRestriction.includes(errorData?.responseMessage)) {
            handle403Error();
          }
          throw new Error(
            JSON.stringify({ error: true, status: 403, data: errorData })
          );
        }
        if (status === 401) {
          throw new Error(
            JSON.stringify({ error: true, status: 401, data: errorData })
          );
        }
        throw new Error(
          JSON.stringify({ error: true, status, data: errorData })
        );
      }

      show403Popup(0, 3);
      throw new Error(
        JSON.stringify({
          error: true,
          status: 0,
          message: error.message || "No response from server",
        })
      );
    }

    throw new Error(
      JSON.stringify({
        error: true,
        status: 0,
        message: "Unexpected error occurred",
      })
    );
  }
};

const show403Popup = (attempts: number, maxAttempts: number) => {
  if (isSessionExpired) return;
  const appStrings = store.getState().app.strings;
  if (appStrings?.t_401_error_popup_title) {
    store.dispatch(
      setPermissionErrorModal({
        title: appStrings.t_401_error_popup_title,
        body: appStrings.t_401_error_popup_body ?? "",
        footer: "",
      })
    );
  } else if (attempts < maxAttempts) {
    setTimeout(() => show403Popup(attempts + 1, maxAttempts), 500);
  } else {
    setTimeout(() => show403Popup(0, maxAttempts), 500);
  }
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const handle403Error = async () => {
  if (isSessionExpired) return;
  await delay(500);
  show403Popup(0, 3);
};
export const postAnaliticsEvent = async (body: any) => {
  if (mockDataEnabled) {
    return;
  }
  const requestUrl = `${BASE_URL}/cmd/event-log`;

  try {
    await axios.post(requestUrl, body, { headers, timeout: API_TIMEOUT_MS });
  } catch (e: any) {
    console.error("postAnaliticsEvent error:", e);
  }
};